import type { ReactElement } from 'react'
import { formatMinorParts } from '~/api/client'

/**
 * A sum of money, with the øre and the unit set apart from the kroner.
 *
 * Two things need the split. The design steps the øre to half size and muted
 * ink, which cannot be done to a string; and a column holding "37,21 kr.",
 * "130,95 €" and "76.221,83 US$" right-aligns on three different suffix widths,
 * so tabular figures line up with nothing until the unit has a slot of its own.
 */
export function Figure({
  amountMinor,
  currency,
  options,
  size = 'figure',
  title,
}: {
  amountMinor: number
  currency: string
  options?: Intl.NumberFormatOptions
  /** `display` is the one figure a page is about; `cell` is a table's. */
  size?: 'display' | 'figure' | 'body' | 'cell'
  title?: string
}): ReactElement {
  const { integer, fraction, unit } = formatMinorParts(amountMinor, currency, options)
  return (
    <span className="nw-figure" data-size={size} title={title}>
      <span className="nw-figure-major">{integer}</span>
      {/* The space stands whether or not there is an øre to separate. Danish
          sets the unit off from the figure, and tying it to the fraction meant
          every sum that came out whole read "26.720.561kr." — which is most of
          them, because the figure a page is about is rounded. */}
      <span className="nw-figure-minor">
        {fraction}{' '}
        <span className="nw-figure-unit">{unit}</span>
      </span>
    </span>
  )
}

const UP = 'M6 2 L10.4 9.4 L1.6 9.4 Z'
const DOWN = 'M6 10 L1.6 2.6 L10.4 2.6 Z'

/**
 * A gain, a loss, or neither.
 *
 * A gain is a solid tinted pill with an upward triangle; a loss is an outlined
 * pill with a downward one. Fill against outline, the arrow's direction and the
 * sign each carry the meaning on their own, so the pair survives a monochrome
 * screen and survives a reader who cannot separate the two hues.
 *
 * A figure that did not move gets no chip at all — plain muted text in the same
 * slot. Cash and property sit at zero most days, and a chip on every one of them
 * turns a column into noise that the two that mean something have to compete
 * with.
 *
 * There is one of it, at one size. The axis that matters is the role, and the
 * sheet reads that off where the chip sits: beside text, or as a card's whole
 * figure. Nothing about it is worth a caller's choosing.
 */
export function DeltaChip({
  children,
  direction,
}: {
  children: string
  direction: 'up' | 'down' | 'flat'
}): ReactElement {
  return (
    <span className="nw-chip" data-direction={direction}>
      {direction !== 'flat' && (
        <svg width={11} height={11} viewBox="0 0 12 12" aria-hidden="true">
          <path d={direction === 'up' ? UP : DOWN} fill="currentColor" />
        </svg>
      )}
      {children}
    </span>
  )
}

export function directionOf(amountMinor: number): 'up' | 'down' | 'flat' {
  if (amountMinor > 0) return 'up'
  if (amountMinor < 0) return 'down'
  return 'flat'
}
